import {
  fleetChargeIdentityFromSerial,
  PermissionCode,
  type FleetChargeStation,
} from '@gb-ticket/shared-types';
import { type FormEvent, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { FleetChargeConnectorCard } from '@/products/fleet/features/fleet-charge-status';
import { fleetPanelClassName } from '@/products/fleet/features/fleet-panel';
import {
  useCreateFleetChargeStation,
  useFleetChargeConnectors,
  useFleetChargeStations,
  usePutFleetChargeTag,
  useRotateFleetChargePassword,
  useUpdateFleetChargeStation,
} from '@/products/fleet/features/use-fleet';

function SavedStation({ station }: { station: FleetChargeStation }) {
  const update = useUpdateFleetChargeStation();
  const rotate = useRotateFleetChargePassword();
  const [name, setName] = useState(station.name);
  const [host, setHost] = useState(station.modbusHost ?? '');
  const [port, setPort] = useState(String(station.modbusPort ?? 502));
  const [unit, setUnit] = useState(
    station.modbusUnitId == null ? '1' : String(station.modbusUnitId),
  );
  const [restUrl, setRestUrl] = useState(station.restBaseUrl ?? '');
  const [restUser, setRestUser] = useState(station.restUsername ?? '');
  const [nextPassword, setNextPassword] = useState('');
  const [shownPassword, setShownPassword] = useState<string | null>(null);

  function fail(error: unknown, fallback: string) {
    toast.error(error instanceof ApiRequestError ? error.message : fallback);
  }

  return (
    <form
      className="space-y-2 border-t pt-3"
      onSubmit={(event) => {
        event.preventDefault();
        void update
          .mutateAsync({
            id: station.id,
            body: {
              name,
              isActive: station.isActive,
              ...(station.interface === 'modbus'
                ? { modbusHost: host, modbusPort: Number(port), modbusUnitId: Number(unit) }
                : {}),
              ...(station.interface === 'rest'
                ? { restBaseUrl: restUrl, restUsername: restUser }
                : {}),
            },
          })
          .then(() => toast.success('Station gespeichert.'))
          .catch((error: unknown) => fail(error, 'Speichern fehlgeschlagen.'));
      }}
    >
      <div className="space-y-1">
        <Label htmlFor={`station-name-${station.id}`}>Name</Label>
        <Input
          id={`station-name-${station.id}`}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      {station.websocketUrl ? (
        <p className="text-sm break-all">WebSocket {station.websocketUrl}</p>
      ) : (
        <p className="text-sm text-muted-foreground">Noch nicht verbunden</p>
      )}
      {station.interface === 'modbus' ? (
        <div className="grid gap-2 sm:grid-cols-3">
          <Input value={host} onChange={(event) => setHost(event.target.value)} aria-label="Host" />
          <Input value={port} onChange={(event) => setPort(event.target.value)} aria-label="Port" />
          <Input
            value={unit}
            onChange={(event) => setUnit(event.target.value)}
            aria-label="Modbus-ID"
          />
        </div>
      ) : null}
      {station.interface === 'rest' ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <Input
            value={restUrl}
            onChange={(event) => setRestUrl(event.target.value)}
            aria-label="REST-Adresse"
          />
          <Input
            value={restUser}
            onChange={(event) => setRestUser(event.target.value)}
            aria-label="Benutzer"
          />
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="outline" disabled={update.isPending}>
          Speichern
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            void update
              .mutateAsync({ id: station.id, body: { name, isActive: !station.isActive } })
              .then(() =>
                toast.success(station.isActive ? 'Station deaktiviert.' : 'Station aktiviert.'),
              )
              .catch((error: unknown) => fail(error, 'Speichern fehlgeschlagen.'));
          }}
        >
          {station.isActive ? 'Deaktivieren' : 'Aktivieren'}
        </Button>
      </div>
      {station.interface === 'ocpp' ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor={`station-password-${station.id}`}>Neues Passwort</Label>
            <Input
              id={`station-password-${station.id}`}
              type="password"
              value={nextPassword}
              onChange={(event) => setNextPassword(event.target.value)}
              minLength={8}
              maxLength={64}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void rotate
                .mutateAsync({ id: station.id, body: { password: nextPassword } })
                .then((result) => {
                  setShownPassword(result.password);
                  setNextPassword('');
                  toast.success('Passwort erneuert. Es steht nur dieses eine Mal da.');
                })
                .catch((error: unknown) => fail(error, 'Passwort nicht erneuert.'));
            }}
          >
            Passwort erneuern
          </Button>
        </div>
      ) : null}
      {shownPassword ? <p className="text-sm">Passwort, nur jetzt: {shownPassword}</p> : null}
    </form>
  );
}

function NetworkPreset({ network }: { network: 'lte' | 'lan' }) {
  if (network === 'lan') {
    return <p className="text-sm">DHCP</p>;
  }
  return (
    <>
      <p className="text-sm">APN emonvia</p>
      <p className="text-sm">Benutzername leer</p>
      <p className="text-sm">Passwort leer</p>
    </>
  );
}

const WALLBOX_CALL_IN_MS = 10 * 60 * 1000;

function ChargeSetupWizard({ stations }: { stations: FleetChargeStation[] }) {
  const create = useCreateFleetChargeStation();
  const [name, setName] = useState('');
  const [serial, setSerial] = useState('');
  const [connectorCount, setConnectorCount] = useState<'1' | '2'>('2');
  const [nameError, setNameError] = useState<string | null>(null);
  const [visitPassword, setVisitPassword] = useState<string | null>(null);
  const [created, setCreated] = useState<{
    id: string;
    ocppIdentity: string | null;
    websocketUrl: string | null;
  } | null>(null);
  const [network, setNetwork] = useState<'lte' | 'lan' | null>(null);
  const [step, setStep] = useState<2 | 3 | 4>(2);
  const [loadShedding, setLoadShedding] = useState(false);
  const [confirmRisk, setConfirmRisk] = useState(false);
  const [confirmWired, setConfirmWired] = useState(false);
  const [waitStopped, setWaitStopped] = useState(false);
  useEffect(() => {
    if (step !== 4) {
      return;
    }
    const timeoutId = window.setTimeout(() => setWaitStopped(true), WALLBOX_CALL_IN_MS);
    return () => window.clearTimeout(timeoutId);
  }, [step]);

  const identity = fleetChargeIdentityFromSerial(serial);
  const existing = identity
    ? stations.find((station) => station.ocppIdentity === identity)
    : undefined;

  async function onContinue(event: FormEvent) {
    event.preventDefault();
    if (visitPassword != null) {
      return;
    }
    const trimmed = name.trim();
    if (trimmed.length < 1) {
      setNameError('Bitte einen Namen angeben.');
      return;
    }
    if (trimmed.length > 80 || !identity || existing) {
      setNameError(null);
      return;
    }
    setNameError(null);
    try {
      const createdStation = await create.mutateAsync({
        name: trimmed,
        interface: 'ocpp',
        connectorCount: connectorCount === '1' ? 1 : 2,
        ocppIdentity: identity,
      });
      setCreated({
        id: createdStation.id,
        ocppIdentity: createdStation.ocppIdentity,
        websocketUrl: createdStation.websocketUrl,
      });
      setVisitPassword(createdStation.password ?? '');
      toast.success('Station angelegt. Das Passwort steht nur dieses eine Mal da.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Station konnte nicht angelegt werden.',
      );
    }
  }

  if (visitPassword != null && step === 4 && network != null) {
    const chargePointId = created?.ocppIdentity ?? identity;
    const reported =
      created != null &&
      stations.some((station) => station.id === created.id && station.lastConnectedAt != null);
    return (
      <section aria-label="Schritt 4" className={`${fleetPanelClassName} space-y-3 p-4`}>
        <h2 className="text-base font-semibold">Schritt 4</h2>
        {visitPassword ? <p className="text-sm">Passwort, nur jetzt: {visitPassword}</p> : null}
        <NetworkPreset network={network} />
        <p className="text-sm">Diese Station als Controller eintragen.</p>
        <p className="text-sm">Den integrierten MID-Zähler verwenden.</p>
        <p className="text-sm">{loadShedding ? 'Lastabwurf an' : 'Lastabwurf aus'}</p>
        <p className="text-sm">OCPP 1.6-J</p>
        <p className="text-sm">ocpp1.6</p>
        {created?.websocketUrl ? (
          <p className="text-sm break-all">WebSocket {created.websocketUrl}</p>
        ) : null}
        {chargePointId ? <p className="text-sm">Charge-Point-ID {chargePointId}</p> : null}
        {chargePointId ? <p className="text-sm">Basic-Benutzername {chargePointId}</p> : null}
        {visitPassword ? <p className="text-sm">Basic-Passwort {visitPassword}</p> : null}
        {reported ? <p className="text-sm">Die Station hat sich gemeldet.</p> : null}
        {waitStopped && !reported ? (
          <p className="text-sm">
            Die Station ist angelegt. Die Wallbox hat sich noch nicht gemeldet.
          </p>
        ) : null}
      </section>
    );
  }

  if (visitPassword != null && step === 3 && network != null) {
    return (
      <section aria-label="Schritt 3" className={`${fleetPanelClassName} space-y-3 p-4`}>
        <h2 className="text-base font-semibold">Schritt 3</h2>
        {visitPassword ? <p className="text-sm">Passwort, nur jetzt: {visitPassword}</p> : null}
        <NetworkPreset network={network} />
        <p className="text-sm">Diese Station als Controller eintragen.</p>
        <p className="text-sm">Den integrierten MID-Zähler verwenden.</p>
        <label className="flex items-center gap-2 text-sm">
          <Switch
            checked={loadShedding}
            onCheckedChange={(checked) => {
              setLoadShedding(checked);
              if (!checked) {
                setConfirmRisk(false);
                setConfirmWired(false);
              }
            }}
          />
          Lastabwurf
        </label>
        {loadShedding ? (
          <>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={confirmRisk}
                onChange={(event) => setConfirmRisk(event.target.checked)}
              />
              Nur aktivieren, wenn zwingend erforderlich, da ansonsten das Laden blockiert werden
              kann
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={confirmWired}
                onChange={(event) => setConfirmWired(event.target.checked)}
              />
              Der Kontakt ist verdrahtet.
            </label>
          </>
        ) : null}
        <Button
          type="button"
          disabled={loadShedding && !(confirmRisk && confirmWired)}
          onClick={() => setStep(4)}
        >
          Weiter
        </Button>
      </section>
    );
  }

  if (visitPassword != null) {
    return (
      <section aria-label="Schritt 2" className={`${fleetPanelClassName} space-y-3 p-4`}>
        <h2 className="text-base font-semibold">Schritt 2</h2>
        {visitPassword ? <p className="text-sm">Passwort, nur jetzt: {visitPassword}</p> : null}
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Netz</legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="charge-network"
              value="lte"
              checked={network === 'lte'}
              onChange={() => setNetwork('lte')}
            />
            LTE
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="charge-network"
              value="lan"
              checked={network === 'lan'}
              onChange={() => setNetwork('lan')}
            />
            LAN
          </label>
        </fieldset>
        {network != null ? <NetworkPreset network={network} /> : null}
        <Button
          type="button"
          disabled={network == null}
          onClick={() => {
            if (network != null) {
              setStep(3);
            }
          }}
        >
          Weiter
        </Button>
      </section>
    );
  }

  return (
    <form
      aria-label="Schritt 1"
      onSubmit={onContinue}
      className={`${fleetPanelClassName} space-y-3 p-4`}
    >
      <h2 className="text-base font-semibold">Schritt 1</h2>
      <div className="space-y-1">
        <Label htmlFor="charge-name">Name</Label>
        <Input
          id="charge-name"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setNameError(null);
          }}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="charge-serial">Seriennummer</Label>
        <Input
          id="charge-serial"
          value={serial}
          onChange={(event) => setSerial(event.target.value)}
        />
      </div>
      {identity ? <p className="text-sm">Kennung {identity}</p> : null}
      {existing ? (
        <>
          <p className="text-sm">{existing.name}</p>
          <p className="text-sm">Diese Kennung ist schon vergeben.</p>
        </>
      ) : null}
      <div className="space-y-1">
        <Label htmlFor="charge-ports">Anschlüsse</Label>
        <select
          id="charge-ports"
          className="flex h-9 w-full rounded-md border bg-transparent px-3 text-sm"
          value={connectorCount}
          onChange={(event) => setConnectorCount(event.target.value === '1' ? '1' : '2')}
        >
          <option value="2">2</option>
          <option value="1">1</option>
        </select>
      </div>
      {nameError ? <p className="text-sm text-destructive">{nameError}</p> : null}
      <Button type="submit" disabled={create.isPending || !identity}>
        Weiter
      </Button>
    </form>
  );
}

export function FleetBetriebLadenPage() {
  usePageTitle('Betrieb — Laden');
  const me = useMe();
  const manage = hasPermission(me.data, PermissionCode.FLEET_TELEMATICS_MANAGE);
  const connectors = useFleetChargeConnectors();
  const stations = useFleetChargeStations(manage);
  const putTag = usePutFleetChargeTag();
  const [idTag, setIdTag] = useState('');
  const [vehicleId, setVehicleId] = useState('');

  async function onTag(event: FormEvent) {
    event.preventDefault();
    try {
      await putTag.mutateAsync({ idTag, vehicleId });
      setIdTag('');
      toast.success('Chip zugeordnet.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Chip konnte nicht zugeordnet werden.',
      );
    }
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Laden"
        description="Ladepunkte der Wallboxen in diesem Mandanten."
      />
      {connectors.isPending ? (
        <p className="text-sm text-muted-foreground">Ladepunkte werden geladen …</p>
      ) : null}
      {connectors.isError ? (
        <p className="text-sm text-destructive">Ladepunkte nicht erreichbar</p>
      ) : null}
      {connectors.data ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {connectors.data.items.map((connector) => (
            <FleetChargeConnectorCard key={connector.id} connector={connector} />
          ))}
        </div>
      ) : null}
      {connectors.data?.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Noch keine Ladestation.</p>
      ) : null}

      {manage ? (
        <div className="space-y-6">
          <ChargeSetupWizard stations={stations.data?.items ?? []} />

          <section className={`${fleetPanelClassName} space-y-3 p-4`}>
            <h2 className="text-base font-semibold">Gespeicherte Stationen</h2>
            {(stations.data?.items ?? []).map((station) => (
              <SavedStation key={station.id} station={station} />
            ))}
          </section>

          <form onSubmit={onTag} className={`${fleetPanelClassName} space-y-3 p-4`}>
            <h2 className="text-base font-semibold">Ladekarte</h2>
            <div className="space-y-1">
              <Label htmlFor="charge-tag">Chip-Nummer</Label>
              <Input
                id="charge-tag"
                value={idTag}
                onChange={(event) => setIdTag(event.target.value)}
                required
                maxLength={20}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="charge-vehicle">Fahrzeug</Label>
              <select
                id="charge-vehicle"
                className="flex h-9 w-full rounded-md border bg-transparent px-3 text-sm"
                value={vehicleId}
                onChange={(event) => setVehicleId(event.target.value)}
                required
              >
                <option value="">Bitte wählen</option>
                {(stations.data?.vehicles ?? []).map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.licensePlate}
                  </option>
                ))}
              </select>
            </div>
            <Button type="submit" disabled={putTag.isPending}>
              Chip zuordnen
            </Button>
            <ul className="space-y-1 text-sm">
              {(stations.data?.tags ?? []).map((tag) => (
                <li key={tag.idTag}>
                  {tag.idTag} · {tag.licensePlate}
                </li>
              ))}
            </ul>
          </form>
        </div>
      ) : null}
    </div>
  );
}
